<template>
  <Teleport to="#modals">
    <WdsModal :show="isOpen" data-test="terminate-contract-modal" @cancel="close">
      <template #header>
        <WdsText xl>
          {{
            $t('Terminating Booking Contract with :worker', {
              worker: workerName,
            })
          }}
        </WdsText>
      </template>

      <template #default>
        <WdsStack direction-column gap-md>
          <WdsStack direction-column gap-xs>
            <WdsText small>
              {{
                $t(
                  'You are about to initiate the early termination of this contract. Before proceeding, please review the instructions and ensure you meet all contractual obligations and any applicable local legal requirements.',
                )
              }}
            </WdsText>
          </WdsStack>

          <WdsStack direction-column gap-lg>
            <WdsStack direction-column gap-lg>
              <WdsStack direction-column gap-sm class="tw-mt-3">
                <WdsText>{{ $t('Proposed end date') }}</WdsText>
                <PickerDatetime
                  v-model="input.date"
                  :placeholder="$t('Select date')"
                  name="date"
                  format="YYYY-MM-DD"
                  :min-date="minDate"
                  :max-date="contractEndDate"
                  data-test="termination-date"
                  :error="date"
                  as-input
                  required
                />

                <WdsText secondary>
                  {{ $t('Select the date you want the contract to end.') }}
                </WdsText>
              </WdsStack>
              <WdsStack direction-column gap-sm>
                <WdsSelect
                  v-model="input.reason"
                  :list="reasons"
                  :label="$t('Termination reason')"
                  :placeholder="$t('Select from list')"
                  data-test="termination-reasons"
                  :error="reason"
                  required
                />
                <WdsText secondary>
                  {{
                    $t(
                      'Select the reason for ending this contract early. This reason will not be shared with the Worker unless required by law.',
                    )
                  }}
                </WdsText>
                <div v-if="input.reason?.value === ContractTerminationReason.Other">
                  <WdsInputArea
                    v-model="input.comments"
                    :error="comments"
                    :placeholder="$t('Specify the reason for termination here')"
                  />

                  <WdsText secondary>
                    {{ $t('Max 500 characters') }}
                  </WdsText>
                </div>
              </WdsStack>

              <WdsInputArea
                v-model="input.message"
                :error="message"
                :label="$t('Write a message to the Worker')"
                :placeholder="
                  $t(
                    'Provide additional context for the Worker if needed. Your message will be visible in the conversation, so ensure it is accurate, appropriate, and compliant with legal obligations.',
                  )
                "
              />
            </WdsStack>
          </WdsStack>

          <WdsAlert warning icon>
            <template #default>{{ $t('If Worksome is the contracting party:') }}</template>

            <template #description>
              {{
                $t(
                  'Once you submit the termination, our team will review the details to confirm compliance with the booking contract and applicable local laws. If local regulations or mandatory notice periods apply, we may need to adjust your proposed termination date. You will be notified if the end date is changed.',
                )
              }}
            </template>
          </WdsAlert>
        </WdsStack>
      </template>

      <template #footer>
        <WdsStack justify-space-between>
          <WdsButton tertiary @click="close">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsStack align-center gap-md>
            <WdsSpinner v-if="loading" indeterminate />

            <WdsButton
              primary
              :disabled="loading"
              data-test="terminate-contract-submit"
              @click="submit"
            >
              {{ $t('Submit') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </Teleport>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsButton,
  WdsInputArea,
  WdsModal,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { reactive, watch, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { required, requiredIf, maxLength } from '@vuelidate/validators'
import NetworkError from '../utils/notification/NetworkError'
import PickerDatetime from '@/components/PickerDatetime.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { TerminateHireMutation } from '@/components/Hire/graphql/mutations'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import { ContractTerminationReason } from '@/gql/graphql'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    hireId: string
    workerName: string
    contractEndDate: string
  }>(),
  {
    isOpen: false,
    hireId: '',
    workerName: '',
    contractEndDate: '',
  },
)

// Needs to be replaced with enum values
const reasons = [
  {
    label: 'Worker Unavailability',
    value: ContractTerminationReason.WorkerUnavailability,
    subLabel: 'The worker is no longer able to fulfil the contract.',
  },
  {
    label: 'Project Completed Early',
    value: ContractTerminationReason.ProjectCompletedEarly,
    subLabel: 'The project was finished ahead of schedule.',
  },
  {
    label: 'Mutual Agreement to Terminate',
    value: ContractTerminationReason.MutualAgreementToTerminate,
    subLabel: 'Both the client and the worker agree to end the contract.',
  },
  {
    label: 'Budget Constraints',
    value: ContractTerminationReason.BudgetConstraints,
    subLabel: 'Financial limitations necessitate ending the contract early.',
  },
  {
    label: 'Change in Project Scope',
    value: ContractTerminationReason.ChangeInProjectScope,
    subLabel: 'Significant alterations in project requirements or objectives.',
  },
  {
    label: 'Performance Issues',
    value: ContractTerminationReason.PerformanceIssues,
    subLabel: 'The work delivered does not meet expected standards.',
  },
  {
    label: 'Communication Issues',
    value: ContractTerminationReason.CommunicationIssues,
    subLabel: 'Challenges in effective communication between parties.',
  },
  {
    label: 'Personal Reasons',
    value: ContractTerminationReason.PersonalReasons,
    subLabel: 'Personal circumstances affecting the Worker’s ability to work.',
  },
  {
    label: 'Legal or Compliance Issues',
    value: ContractTerminationReason.LegalOrComplianceIssues,
    subLabel: 'Legal matters or compliance regulations require termination.',
  },
  {
    label: 'Violation of Contract Terms',
    value: ContractTerminationReason.ViolationOfContractTerms,
    subLabel: 'Breach of agreed-upon terms by one of the parties.',
  },
  {
    label: 'Unforeseen Circumstances (Force Majeure)',
    value: ContractTerminationReason.UnforeseenCircumstances,
    subLabel: 'Events beyond control, such as natural disasters or emergencies.',
  },
  {
    label: 'Dissatisfaction with Quality of Work',
    value: ContractTerminationReason.DissatisfactionWithQualityOfWork,
    subLabel: 'The quality of work is not satisfactory to the client.',
  },
  {
    label: 'Conflict of Interest',
    value: ContractTerminationReason.ConflictOfInterest,
    subLabel: 'A conflict has arisen that affects the working relationship.',
  },
  {
    label: 'Other (Please Specify)',
    value: ContractTerminationReason.Other,
    subLabel: 'Any reason not covered above.',
  },
]

const minDate = new Date().toDateString()

const emit = defineEmits(['closed'])

interface TerminationInput {
  date: string
  reason: ListItem | null
  comments: string
  message: string
}

const defaultInput: TerminationInput = {
  date: '',
  reason: null,
  comments: '',
  message: '',
}

const input = reactive<TerminationInput>({ ...defaultInput })

const resetInput = () => {
  Object.assign(input, defaultInput)
}

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      resetInput()
    }
  },
)

const {
  loading,
  mutate: terminateHire,
  error,
} = useMutation(TerminateHireMutation, () => ({
  clientId: 'publicApi',
  refetchQueries: queriesToRefetch,
}))

const commentsRequired = () => input.reason?.value === ContractTerminationReason.Other

const vuelidate = useCustomVuelidate(
  {
    date: { required },
    reason: { required },
    comments: {
      requiredIfClient: requiredIf(commentsRequired),
      maxLength: maxLength(500),
    },
    message: {},
  },
  input,
)

const apollo = useApolloError(error)
const { date, reason, comments, message } = useApolloAndVuelidate(apollo, vuelidate, 'input')

const errorMessage = ref<string | null>(null)

const clearError = () => {
  errorMessage.value = null
}

const submit = async () => {
  clearError()
  vuelidate.touchAll()

  if (!vuelidate.error.value) {
    try {
      await terminateHire({
        input: {
          hire: props.hireId,
          date: input.date,
          reason: input.reason?.value,
          comments: input.comments,
          message: input.message,
        },
      })

      emit('closed')
    } catch (ex: any) {
      new NetworkError({ ex }).dispatch()
    }
  }
}

const close = () => {
  emit('closed')
}
</script>
